import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { getOrderUuid, getSelectedExhibition } from '@app/app.reducer';
import { Store } from '@ngrx/store';
import {
  BookingContingentReservation,
  BookingPackageType,
  RemoveProductBookingContingentReservation,
  SetBookingContingentReservation,
  SetProductBookingContingentReservation,
  isBookingProductTypeTariff
} from '@products/models/booking.model';
import {
  CreateContingentReservationsResponse,
  CreateContingentTariffReservation,
  CreateContingentTariffToBookingContingentReservationsMapper,
  PostCreateContingentReservations,
  RemoveContingentReservation,
  SetContingentReservation,
  SetContingentReservationDate
} from '@products/models/contingent.model';
import { TariffUuids } from '@products/models/tariff.model';
import { AppConstants } from '@shared/app-constants';
import { environment } from '@src/environments/environment';
import { ExhibitionModel } from '@store/exhibition/exhibition.interface';
import { HelperService } from '@store/helpers/helper.service';
import { cloneDeep, isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { Observable, combineLatest, first, map, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ContingentBookingService {
  private readonly store = inject(Store);
  private readonly http = inject(HttpClient);
  private readonly helperService = inject(HelperService);

  setBookingContingentReservations(tariffCount: number, hasDaySellLimit: boolean, tariffUuids: TariffUuids) {
    return hasDaySellLimit && tariffCount > 0
      ? Array.from(
          Array(tariffCount),
          (_, index): BookingContingentReservation => ({
            tariffUuid: tariffUuids[index],
            duration: null,
            isValid: null,
            fromDate: null,
            toDate: null
          })
        )
      : [];
  }

  postCreateContingentReservations$(
    createContingentReservations: PostCreateContingentReservations
  ): Observable<CreateContingentReservationsResponse[]> {
    return this.http.post<CreateContingentReservationsResponse[]>(
      `${environment.protocol}${environment.webApiUrl}/order/day-booking`,
      createContingentReservations
    );
  }

  postChangeTariffContingentDay$(tariffId: number, day: any) {
    return this.http.post(
      `${environment.protocol}${environment.webApiUrl}/user/entry-tickets/${tariffId}/change-day-booking`,
      { day }
    );
  }

  postReleaseContingentReservations$(uuid: string) {
    return this.http.post<void>(
      `${environment.protocol}${environment.webApiUrl}/order/day-booking/${uuid}/release`,
      {}
    );
  }

  setContingentReservations$(
    createContingentReservationsMappers: CreateContingentTariffToBookingContingentReservationsMapper[]
  ): Observable<SetContingentReservation> {
    return combineLatest([this.store.select(getSelectedExhibition), this.store.select(getOrderUuid)]).pipe(
      first((combinedLatest) => combinedLatest.every((combinedItem) => !!combinedItem)),
      map(([selectedExhibition, orderUuid]): SetContingentReservation => {
        return {
          selectedExhibition,
          orderUuid,
          reservations: this.mapCreateContingentMappersToCreateContingentTariffReservations(
            createContingentReservationsMappers
          )
        };
      })
    );
  }

  releaseAllContingentReservations$() {
    return this.store.select(getOrderUuid).pipe(
      first((orderUuid) => !isEmpty(orderUuid)),
      switchMap((orderUuid) => this.postReleaseContingentReservations$(orderUuid).pipe(map(() => orderUuid)))
    );
  }

  removeBookingContingentReservationIndexesFromSortedBookingContingentReservations(
    removeContingentReservations: RemoveContingentReservation[],
    sortedProductBookingContingentReservations: SetProductBookingContingentReservation[]
  ) {
    removeContingentReservations.forEach((removeContingentReservation) => {
      const { ticketTypeId, ticketPersonId, packageNumber, packageIndex, removeBookingReservationIndexes } =
        removeContingentReservation;

      const bookedContingentReservation = sortedProductBookingContingentReservations.find(
        (bookingContingentReservation) =>
          bookingContingentReservation.ticketTypeId === ticketTypeId &&
          bookingContingentReservation.ticketPersonId === ticketPersonId &&
          bookingContingentReservation.packageNumber === packageNumber &&
          bookingContingentReservation.packageIndex === packageIndex
      );

      if (bookedContingentReservation) {
        removeBookingReservationIndexes
          .reverse()
          .forEach((bookingReservationIndex) =>
            bookedContingentReservation.contingentReservations.splice(bookingReservationIndex, 1)
          );
      }
    });
  }

  mapSetContingentDateToSetProductBookingContingentReservations(
    contingentReservationDates: SetContingentReservationDate[],
    sortedProductBookingContingentReservations: SetProductBookingContingentReservation[]
  ) {
    contingentReservationDates.forEach((contingentReservationDate) => {
      const sortedContingentReservationIndex = sortedProductBookingContingentReservations.findIndex(
        (sortedContingentReservation) =>
          sortedContingentReservation.ticketTypeId === contingentReservationDate.ticketTypeId &&
          sortedContingentReservation.ticketPersonId === contingentReservationDate.ticketPersonId &&
          sortedContingentReservation.voucherCode === contingentReservationDate.voucherCode &&
          sortedContingentReservation.packageNumber === contingentReservationDate.packageNumber &&
          sortedContingentReservation.packageIndex === contingentReservationDate.packageIndex
      );

      if (sortedContingentReservationIndex !== AppConstants.INDEX_NOT_FOUND) {
        const sortedProductBookingContingentReservation: SetProductBookingContingentReservation =
          sortedProductBookingContingentReservations[sortedContingentReservationIndex];

        sortedProductBookingContingentReservation.contingentReservations[
          contingentReservationDate.bookingReservationIndex
        ].fromDate = contingentReservationDate.day;
      }
    });
  }

  mapSortedProductBookingContingentReservationsToCreateContingentReservationMappers(
    sortedProductBookingContingentReservations: SetProductBookingContingentReservation[]
  ): CreateContingentTariffToBookingContingentReservationsMapper[] {
    const createContingentReservationsMappers: CreateContingentTariffToBookingContingentReservationsMapper[] = [];

    sortedProductBookingContingentReservations.forEach((sortedContingentReservation) =>
      sortedContingentReservation.contingentReservations.forEach((contingentReservation, bookingReservationIndex) => {
        if (contingentReservation.fromDate) {
          const {
            productType,
            ticketTypeId,
            ticketPersonId,
            ticketPersonTypeId,
            voucherCode,
            packageNumber,
            packageIndex
          } = sortedContingentReservation;
          const { tariffUuid, duration, fromDate, toDate, isValid } = contingentReservation;
          const uniqueId = packageNumber
            ? `${ticketTypeId}_${ticketPersonTypeId}_${packageNumber}_${packageIndex}`
            : `${ticketTypeId}_${ticketPersonTypeId}`;

          const createContingentReservationMapper: CreateContingentTariffToBookingContingentReservationsMapper = {
            productType,
            tariffUuid,
            duration,
            fromDate,
            toDate,
            isValid,
            bookingReservationIndex,
            ticketIndex: createContingentReservationsMappers.length,
            ticketTypeId,
            ticketPersonId,
            ticketPersonTypeId,
            voucherCode,
            packageNumber,
            packageIndex,
            uniqueId
          };

          createContingentReservationsMappers.push(createContingentReservationMapper);
        }
      })
    );

    return createContingentReservationsMappers;
  }

  mapCreateContingentReservationResponseToSetProductBookingContingentReservations(
    selectedExhibition: ExhibitionModel,
    createContingentReservationResponse: CreateContingentReservationsResponse[],
    createContingentTariffToBookingContingentReservationsMappers: CreateContingentTariffToBookingContingentReservationsMapper[]
  ): SetProductBookingContingentReservation[] {
    const setProductBookingContingentReservations: SetProductBookingContingentReservation[] = [];

    createContingentReservationResponse.forEach((contingentReservationResponse) => {
      const contingentReservationMapper = createContingentTariffToBookingContingentReservationsMappers.find(
        (bookingContingentReservationMapper) =>
          bookingContingentReservationMapper.ticketIndex === contingentReservationResponse.ticketIndex
      );

      if (contingentReservationMapper) {
        const {
          productType,
          ticketTypeId,
          ticketPersonId,
          ticketPersonTypeId,
          voucherCode,
          packageNumber,
          packageIndex,
          tariffUuid,
          fromDate,
          toDate,
          duration,
          bookingReservationIndex
        } = contingentReservationMapper;

        const selectedExhibitionClone: ExhibitionModel = cloneDeep(selectedExhibition);
        const selectedExhibitionEndDate = selectedExhibitionClone.endDate as Date;
        const fromDateTime: DateTime = DateTime.fromJSDate(fromDate);
        const isMultiDayDuration = duration > 1;
        let validatedDuration = duration;
        let validatedToDate: Date = isMultiDayDuration
          ? fromDateTime.plus({ days: duration - 1 }).toJSDate()
          : fromDate;

        const isValidatedToDateGreaterThenToDate = this.helperService.isFirstDateGreaterWOTime(validatedToDate, toDate);

        if (isValidatedToDateGreaterThenToDate) {
          validatedToDate = toDate;
        }

        const isValidatedToDateGreaterThenExhibitionEndDate = this.helperService.isFirstDateGreaterWOTime(
          validatedToDate,
          selectedExhibitionEndDate
        );

        if (isValidatedToDateGreaterThenExhibitionEndDate) {
          validatedToDate = selectedExhibitionEndDate;
        }

        const areFromAndToDateEqual = this.helperService.areDatesSameWOTime(fromDate, validatedToDate);

        if (isMultiDayDuration && areFromAndToDateEqual) {
          validatedDuration = 1;
        }

        const setBookingContingentReservation: SetBookingContingentReservation = {
          tariffUuid,
          fromDate,
          toDate: validatedToDate,
          duration: validatedDuration,
          isValid: contingentReservationResponse.isValid,
          bookingReservationIndex
        };

        const alreadyCreatedSetProductBookingContingentReservation = setProductBookingContingentReservations.find(
          (setBookingContingentReservation) =>
            setBookingContingentReservation.ticketTypeId === ticketTypeId &&
            setBookingContingentReservation.ticketPersonId === ticketPersonId &&
            setBookingContingentReservation.voucherCode === voucherCode &&
            setBookingContingentReservation.packageNumber === packageNumber &&
            setBookingContingentReservation.packageIndex === packageIndex
        );

        if (alreadyCreatedSetProductBookingContingentReservation) {
          alreadyCreatedSetProductBookingContingentReservation.contingentReservations.push(
            setBookingContingentReservation
          );
        } else {
          setProductBookingContingentReservations.push({
            productType,
            ticketTypeId,
            ticketPersonId,
            ticketPersonTypeId,
            voucherCode,
            packageNumber,
            packageIndex,
            contingentReservations: [setBookingContingentReservation]
          });
        }
      }
    });

    return setProductBookingContingentReservations;
  }

  mapRemoveContingentReservationsToRemoveProductBookingContingentReservation(
    removeContingentReservations: RemoveContingentReservation[],
    createContingentTariffToBookingContingentReservationsMappers: CreateContingentTariffToBookingContingentReservationsMapper[]
  ): RemoveProductBookingContingentReservation[] {
    const removeProductBookingContingentReservations: RemoveProductBookingContingentReservation[] = [];

    removeContingentReservations.forEach((removeContingentReservation) => {
      const { ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex } = removeContingentReservation;
      const removeProductBookingMapper = createContingentTariffToBookingContingentReservationsMappers.find(
        (bookingContingentReservationMapper) =>
          bookingContingentReservationMapper.ticketTypeId === ticketTypeId &&
          bookingContingentReservationMapper.ticketPersonId === ticketPersonId &&
          bookingContingentReservationMapper.voucherCode === voucherCode &&
          bookingContingentReservationMapper.packageNumber === packageNumber &&
          bookingContingentReservationMapper.packageIndex === packageIndex
      );

      if (removeProductBookingMapper) {
        const removeProductBookingContingentReservation: RemoveProductBookingContingentReservation = {
          productType: removeProductBookingMapper.productType,
          ticketTypeId,
          ticketPersonId,
          voucherCode,
          packageNumber,
          packageIndex,
          removeBookingReservationIndexes: removeContingentReservation.removeBookingReservationIndexes
        };

        removeProductBookingContingentReservations.push(removeProductBookingContingentReservation);
      }
    });

    return removeProductBookingContingentReservations;
  }

  mapRemovedBookingPackageTypesToRemoveContingentReservations(
    removedBookingPackageTypes: BookingPackageType[]
  ): RemoveContingentReservation[] {
    const removeContingentReservations: RemoveContingentReservation[] = [];

    removedBookingPackageTypes.forEach((removedBookingPackageType) => {
      removedBookingPackageType.productTypes.forEach((removedBookingPackageProductType) => {
        if (isBookingProductTypeTariff(removedBookingPackageProductType)) {
          removedBookingPackageProductType.tariffs.forEach((removedBookingPackageTariff) => {
            const { ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex, contingentReservations } =
              removedBookingPackageTariff;

            const contingentReservationIndexesToRemove: number[] = [];

            contingentReservations.forEach((contingentReservation, contingentReservationIndex) => {
              if (contingentReservation.fromDate) {
                contingentReservationIndexesToRemove.push(contingentReservationIndex);
              }
            });

            const removeContingentReservation: RemoveContingentReservation = {
              ticketTypeId,
              ticketPersonId,
              voucherCode,
              packageNumber,
              packageIndex,
              removeBookingReservationIndexes: contingentReservationIndexesToRemove
            };

            if (contingentReservationIndexesToRemove.length) {
              removeContingentReservations.push(removeContingentReservation);
            }
          });
        }
      });
    });

    return removeContingentReservations;
  }

  private mapCreateContingentMappersToCreateContingentTariffReservations(
    createContingentTariffToBookingContingentReservationsMappers: CreateContingentTariffToBookingContingentReservationsMapper[]
  ): CreateContingentTariffReservation[] {
    const createContingentTariffReservations: CreateContingentTariffReservation[] = [];

    createContingentTariffToBookingContingentReservationsMappers.forEach((contingentReservationMapper) =>
      createContingentTariffReservations.push({
        day: DateTime.fromJSDate(contingentReservationMapper.fromDate).toISODate(),
        ticketPersonId: contingentReservationMapper.ticketPersonId,
        ticketIndex: contingentReservationMapper.ticketIndex,
        uniqueId: contingentReservationMapper.uniqueId
      })
    );

    return createContingentTariffReservations;
  }
}
